import React, { useRef, useState } from "react";
import { useHistory } from "react-router";

import {
  Backdrop,
  CircularProgress,
  DialogContentText,
  makeStyles,
} from "@material-ui/core";

import { useNotify, useRedirect, SaveButton, useRefresh } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { logError } from "../../../../utils/appUtils";
import { NoteType } from "../../../../utils/constants";
import { deepRedirect } from "../../../../utils/utils";
import {
  calculatePickListQuantityState,
  deleteLambdaPickListItem,
  updateOutboundQuantity,
  validateShipmentLineItemBeforeSave,
} from "./PickListItemUtils";
import {
  formValidate,
  getAllInventories,
  validateReleasedRailcarCommit,
} from "../OutboundUtils";
import { ConditionalConfirm } from "../../../../components/common/ConditionalConfirm";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const validateNotes = (form, formData) => {
  const notesMaxIndex = formData.notes ? formData.notes.length - 1 : -1;
  const qtyState = form.getFieldState("totalQuantityToPick");
  const quantityChanged = qtyState?.dirty;
  debugger;

  if (
    quantityChanged &&
    (notesMaxIndex < 0 ||
      (notesMaxIndex >= 0 &&
        (!formData.notes[notesMaxIndex] || formData.notes[notesMaxIndex].date)))
  ) {
    //Trigger Add a Note to document changes
    return true;
  }
  return false;
};

export const DialogSaveButton = ({
  record,
  outbound,
  backPath,
  isSaving,
  setIsSaving,
  isDeleting,
  setAllInventories,
  setIsLoaded,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const formState = form.getState();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Save Confirmation");
  const [confirmContent, setConfirmContent] = useState("");
  const saveInFlightRef = useRef(false);

  const lockSave = () => {
    if (saveInFlightRef.current) return false;
    saveInFlightRef.current = true;
    setIsSaving(true);
    return true;
  };

  const unlockSave = () => {
    saveInFlightRef.current = false;
    setIsSaving(false);
  };

  const handleDialogConfirm = () => handleSave(formState.values);
  const handleDialogClose = () => {
    setOpen(false);
    unlockSave();
  };

  const [onConfirm, setOnConfirm] = useState(() => handleDialogConfirm);

  const handleValidateSave = async (props) => {
    const formState = form.getState();
    const formData = formState.values;
    const tempMsg = [];
    setOnConfirm(() => () => handleSave(formData));
    debugger;

    const isValid = formValidate(form, notify);
    if (!isValid) {
      handleSave(formData, { skipLock: true });
      return;
    }

    if (!lockSave()) return;

    try {
      // Stale outbound+lineItem+inventory validation:
      const { staleErrors } = await validateShipmentLineItemBeforeSave(
        formState,
        notify,
      );

      // If any staleErrors: force a refresh via Confirm
      if (staleErrors.length > 0) {
        getAllInventories(
          formData.accountId,
          formData.outbound?.containerPickup,
          setAllInventories,
          setIsLoaded,
          notify,
        );

        setConfirmContent(
          <div>
            <p>Data was modified by another user while you were editing:</p>
            <ul>
              {staleErrors.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
            <p>Confirm to refresh and apply your edits again.</p>
          </div>,
        );
        setOnConfirm(() => () => {
          setOpen(false);
          refresh();
        });
        unlockSave();
        setOpen(true);
        return;
      }

      // check if all line items deleted or zeroed-out
      const allZeros =
        !Array.isArray(formData.inventoryItems?.items) ||
        !formData.inventoryItems?.items?.length ||
        formData.inventoryItems.items.every(
          (ii) => Number(ii?.quantityToPick || 0) === 0,
        );
      const totalQtyZero = Number(formData.totalQuantityToPick || 0) === 0;
      // Inventory still in a railcar that has left cannot be committed — a
      // fresh read, since the dialog's list may predate the release.
      const releasedRefusal = await validateReleasedRailcarCommit([formData]);
      if (releasedRefusal) {
        tempMsg.push(<DialogContentText>{releasedRefusal}</DialogContentText>);
        setTitle("Save Confirmation");
        setOnConfirm(null); // Prevent confirmation, only cancel
      } else if (allZeros || totalQtyZero) {
        tempMsg.push(
          <DialogContentText>
            All Inventory items were removed. This Pick List record will be
            deleted!
            <br />
            Delete this Pick List record?
          </DialogContentText>,
        );
        setTitle("Delete Confirmation");
        setOnConfirm(() => async () => {
          setOpen(false);
          await handleDelete(formData);
        });
      } else if (validateNotes(form, formData)) {
        tempMsg.push(
          <DialogContentText>
            Don't forget to document all quantity changes in the Notes!
            <br />
            Proceed without adding a Note?
          </DialogContentText>,
        );
        setTitle("Save Confirmation");
      }
    } catch (error) {
      logError("lineItem:DialogSaveButton:handleValidateSave", error, notify);
      unlockSave();
      return;
    }

    if (tempMsg.length === 1) {
      setConfirmContent(tempMsg[0]);
      setOpen(true);
      unlockSave();
    } else if (tempMsg.length > 1) {
      setConfirmContent(
        <ul>
          {tempMsg.map((msg) => (
            <li>{msg}</li>
          ))}
        </ul>,
      );
      setOpen(true);
      unlockSave();
    } else handleSave(formData, { skipLock: true });
  };

  const handleSave = async (formData, { skipLock = false } = {}) => {
    if (!skipLock && !lockSave()) return;
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const handleDelete = async (data) => {
    debugger;
    setIsSaving(true);
    try {
      const response = await deleteLambdaPickListItem(record);
      if (response.error) throw response.error;

      await updateOutboundQuantity(
        outbound,
        data,
        record.totalQuantityToPick,
        0,
      );

      notify("Pick List Item deleted successfully", "success");
      deepRedirect(history, backPath);
    } catch (error) {
      logError(
        "lineItem:DialogSaveButton:handleRemove",
        error,
        notify,
        redirect,
      );
    } finally {
      unlockSave();
    }
  };

  const transform = async (data) => {
    setIsSaving(true);
    debugger;

    const updateDate = new Date();
    data.outboundShipment = { ...outbound };
    const initQty = record.totalQuantityToPick;
    const quantityState = calculatePickListQuantityState(record, data);
    data.totalQuantityToPick = quantityState.currentTotalQuantityToPick;
    data.inventory.quantityCommitted = quantityState.inventoryQuantityCommitted;

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = updateDate;
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    if (initQty && initQty !== data.totalQuantityToPick) {
      // quantity changed
      const note = {
        type: NoteType.INVENTORY,
        date: updateDate,
        by: window.appUser?.fullName,
        text: `Outbound Qty. Changed from ${initQty} to ${data.totalQuantityToPick}`,
      };

      // shipment LineItem history
      data.history = [...data.history, note];
    }
    delete data.quantityAvailable;
    return data;
  };

  const onSuccess = async ({ data }) => {
    debugger;
    try {
      notify("Pick List Item updated successfully", "success");
      deepRedirect(history, backPath);
    } catch (error) {
      logError("lineItem:DialogSaveButton:onSuccess", error, notify, redirect);
      return;
    } finally {
      unlockSave();
    }
  };

  const onFailure = (error) => {
    unlockSave();
    logError("lineItem:DialogSaveButton:onFailure", error, notify);
  };

  const { pristine, submitting } = useFormState({ subscription: { pristine: true, submitting: true } });

  // Pass ...props, not ...rest to SaveButton to include handleSubmitWithRedirect from props
  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        {...props}
        label="Save"
        submitOnEnter={false}
        undoable={false}
        disabled={pristine || isSaving || isDeleting}
        saving={isSaving || submitting}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title={title}
        content={confirmContent}
        onConfirm={onConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
